import { createVuetify } from "vuetify";
import palette from "@/guac-layout/styles/variables.module.scss";

import * as vuetifyComponents from "vuetify/lib/components/index.mjs";

// The shared SystemJS bundle exposes components under `components`; local Jest imports expose them directly.
const {
  VAppBar,
  VAppBarNavIcon,
  VIcon,
  VLayout,
  VList,
  VListGroup,
  VListItem,
  VListItemTitle,
  VNavigationDrawer,
  VSpacer,
} = (vuetifyComponents as any).components ?? vuetifyComponents;

if (process.env.VUE_APP_STANDALONE === "true") {
  require("@mdi/font/css/materialdesignicons.css");
  require("vuetify/lib/styles/main.sass");
  // Getwell fonts
  require("../styles/fonts.scss");
}

export default createVuetify({
  theme: {
    defaultTheme: "gwPurple",
    variations: {
      colors: ["primary", "dark", "light", "error", "info", "warning"],
      lighten: 5,
      darken: 5,
    },
    themes: {
      // Themes must be defined within this object. They cannot be defined as variables outside of createVuetify
      gwPurple: {
        dark: false,
        colors: {
          primary: palette.gwPurple,
          dark: palette.gwDarkPurple,
          light: palette.gwLightPurple,
          background: palette.gwBackgroundPurple,
          dark20: palette.gwPrimaryDarkPurple20,
          dark70: palette.gwPrimaryDarkPurple70,
          error: palette.gwErrorRed,
          info: palette.gwInfoBlue,
          success: palette.gwValidationGreen,
          warning: palette.gwWarningYellow,
        },
      },
      whitelabel: {
        dark: false,
        colors: {
          primary: palette.whitelabelPrimary,
          "on-dark": palette.black,
          dark: palette.whitelabelDark,
          light: palette.whitelabelLight,
          background: palette.whitelabelBackground,
          primary20: palette.whitelabelDark20,
          dark20: palette.whitelabelDark20,
          dark70: palette.whitelabelDark70,
          error: palette.gwErrorRed,
          info: palette.gwInfoBlue,
          success: palette.gwValidationGreen,
          warning: palette.gwWarningYellow,
        },
      },
      blue: {
        dark: false,
        colors: {
          primary: palette.bluePrimary,
          dark: palette.blueDark,
          light: palette.blueLight,
          background: palette.blueBackground,
          primary20: palette.blueDark20,
          dark20: palette.blueDark20,
          dark70: palette.blueDark70,
          error: palette.gwErrorRed,
          info: palette.gwInfoBlue,
          success: palette.gwValidationGreen,
          warning: palette.gwWarningYellow,
        },
      },
      brown: {
        dark: false,
        colors: {
          primary: palette.brownPrimary,
          dark: palette.brownDark,
          light: palette.brownLight,
          background: palette.brownBackground,
          primary20: palette.brownDark20,
          dark20: palette.brownDark20,
          dark70: palette.brownDark70,
          error: palette.gwErrorRed,
          info: palette.gwInfoBlue,
          success: palette.gwValidationGreen,
          warning: palette.gwWarningYellow,
        },
      },
      green: {
        dark: false,
        colors: {
          primary: palette.greenPrimary,
          dark: palette.greenDark,
          light: palette.greenLight,
          background: palette.greenBackground,
          dark20: palette.greenDark20,
          dark70: palette.greenDark70,
          "on-primary": palette.black,
          error: palette.gwErrorRed,
          info: palette.gwInfoBlue,
          success: palette.gwValidationGreen,
          warning: palette.gwWarningYellow,
        },
      },
      purple: {
        dark: false,
        colors: {
          primary: palette.purplePrimary,
          dark: palette.purpleDark,
          light: palette.purpleLight,
          background: palette.purpleBackground,
          dark20: palette.purpleDark20,
          dark70: palette.purpleDark70,
          error: palette.gwErrorRed,
          info: palette.gwInfoBlue,
          success: palette.gwValidationGreen,
          warning: palette.gwWarningYellow,
        },
      },
      teal: {
        dark: false,
        colors: {
          primary: palette.tealPrimary,
          dark: palette.tealDark,
          light: palette.tealLight,
          background: palette.tealBackground,
          dark20: palette.tealDark20,
          dark70: palette.tealDark70,
          "on-primary": palette.white,
          error: palette.gwErrorRed,
          info: palette.gwInfoBlue,
          success: palette.gwValidationGreen,
          warning: palette.gwWarningYellow,
        },
      },
      deepBlue: {
        dark: false,
        colors: {
          primary: palette.deepBluePrimary,
          dark: palette.deepBlueDark,
          light: palette.deepBlueLight,
          background: palette.deepBlueBackground,
          primary20: palette.deepBlueDark20,
          light20: palette.deepBlueBackground,
          dark20: palette.deepBlueDark20,
          dark70: palette.deepBlueDark70,
          error: palette.gwErrorRed,
          info: palette.gwInfoBlue,
          success: palette.gwValidationGreen,
          warning: palette.gwWarningYellow,
        },
      },
      deepGreen: {
        dark: false,
        colors: {
          primary: palette.deepGreenPrimary,
          dark: palette.deepGreenDark,
          light: palette.deepGreenLight,
          background: palette.deepGreenBackground,
          dark20: palette.deepGreenDark20,
          dark70: palette.deepGreenDark70,
          error: palette.gwErrorRed,
          info: palette.gwInfoBlue,
          success: palette.gwValidationGreen,
          warning: palette.gwWarningYellow,
        },
      },
      deepTeal: {
        dark: false,
        colors: {
          primary: palette.deepTealPrimary,
          dark: palette.deepTealDark,
          light: palette.deepTealLight,
          background: palette.deepTealBackground,
          dark20: palette.deepTealDark20,
          dark70: palette.deepTealDark70,
          error: palette.gwErrorRed,
          info: palette.gwInfoBlue,
          success: palette.gwValidationGreen,
          warning: palette.gwWarningYellow,
        },
      },
    },
  },
  components: {
    VAppBar,
    VAppBarNavIcon,
    VIcon,
    VLayout,
    VList,
    VListGroup,
    VListItem,
    VListItemTitle,
    VNavigationDrawer,
    VSpacer,
  },
  defaults: {
    VAlert: {
      class: "gw-alert",
      type: "info",
      icon: "mdi-information-outline",
      rounded: "lg",
      variant: "tonal",
      style: {
        color: palette.black,
      },
    },
    VAlertError: {
      class: "gw-alert",
      type: "error",
      icon: "mdi-alert-circle-outline",
      rounded: "lg",
      variant: "tonal",
    },
    VAlertSuccess: {
      class: "gw-alert",
      type: "success",
      icon: "mdi-check-circle-outline",
      rounded: "lg",
      variant: "tonal",
    },
    VAlertWarn: {
      class: "gw-alert",
      type: "warning",
      icon: "mdi-alert-outline",
      rounded: "lg",
      variant: "tonal",
    },
    VAppBar: {
      style: {
        backgroundColor: palette.white,
        color: "rgb(var(--v-theme-dark))",
      },
      VAppBarNavIcon: {
        style: {
          style: {
            backgroundColor: palette.white,
            color: "rgb(var(--v-theme-dark))",
          },
        },
      },
    },
    VAvatar: {
      style: {
        backgroundColor: "rgb(var(--v-theme-background))",
        color: "rgb(var(--v-theme-dark))",
      },
    },
    VBadge: {
      color: palette.gwRed,
    },
    VBottomNav: {
      grow: "true",
      style: {
        backgroundColor: "transparent",
      },
      VBtn: {
        variant: "flat",
        style: {
          color: "rgb(var(--v-theme-dark))",
        },
      },
    },
    VBtnNav: {
      variant: "flat",
      style: {
        backgroundColor: "transparent",
        color: "rgb(var(--v-theme-dark))",
      },
    },
    VBreadcrumbs: {
      color: "dark",
      VIcon: {
        style: {
          color: "rgb(var(--v-theme-dark))",
        },
      },
      VBreadcrumbsDivider: {
        style: {
          color: "rgb(var(--v-theme-dark))",
        },
      },
    },
    VBtnPrimary: {
      class: "btn-primary",
      rounded: "pill",
      variant: "flat",
      color: "primary",
      style: {
        color: palette.white,
      },
      VProgressCircular: {
        color: palette.white,
      },
    },
    VBtnWhite: {
      class: "btn-white",
      rounded: "pill",
      variant: "flat",
      style: {
        backgroundColor: palette.white,
        color: "rgb(var(--v-theme-dark))",
      },
    },
    VBtnLight: {
      class: "btn-light",
      rounded: "pill",
      variant: "flat",
      color: "light",
      style: {
        color: "rgb(var(--v-theme-dark))",
      },
    },
    VBtnDark: {
      class: "btn-dark",
      rounded: "pill",
      variant: "flat",
      color: "dark",
    },
    VBtnOutlineDark: {
      class: "btn-outlined",
      rounded: "pill",
      variant: "outlined",
      style: {
        backgroundColor: palette.white,
        border: "1px solid rgb(var(--v-theme-dark20))",
        color: "rgb(var(--v-theme-dark))",
      },
    },
    VBtnOutlineWhite: {
      class: "btn-outlined",
      rounded: "pill",
      variant: "outlined",
      style: {
        backgroundColor: "transparent",
        border: "1px solid #fff",
        color: palette.white,
      },
    },
    VBtnTextPrimary: {
      class: "btn-text-primary",
      rounded: "pill",
      variant: "flat",
      color: "transparent",
    },
    VBtnTextSecondary: {
      class: "btn-text-secondary",
      rounded: "pill",
      variant: "flat",
      color: "transparent",
    },
    VBtnTextDark: {
      class: "btn-text-dark",
      rounded: "pill",
      variant: "flat",
      color: "transparent",
      style: {},
    },
    VBtnTextWhite: {
      class: "btn-text-white",
      rounded: "pill",
      variant: "flat",
      color: "transparent",
      style: {},
    },
    VBtnIcon: {
      class: "btn-primary",
      variant: "flat",
      color: "primary",
      style: {
        color: palette.white,
      },
    },
    VBtnIconWhite: {
      class: "btn-white",
      variant: "flat",
      style: {
        color: "rgb(var(--v-theme-dark))",
      },
    },
    VBtnIconLight: {
      class: "btn-light",
      variant: "flat",
      color: "light",
      style: {
        color: "rgb(var(--v-theme-dark))",
      },
    },
    VBtnIconDark: {
      class: "btn-dark",
      variant: "flat",
      color: "dark",
      style: {},
    },
    VBtnIconOutline: {
      class: "btn-outlined",
      variant: "outline",
      style: {
        backgroundColor: palette.white,
        border: "1px solid rgb(var(--v-theme-dark20))",
        color: "rgb(var(--v-theme-dark))",
      },
    },
    VBtnFab: {
      elevation: 4,
      style: {
        backgroundColor: "rgb(var(--v-theme-primary))",
        color: palette.white,
      },
    },
    VBtnFabWhite: {
      elevation: 4,
      style: {
        backgroundColor: palette.white,
        color: "rgb(var(--v-theme-dark))",
      },
    },
    VBtnFabLight: {
      elevation: 4,
      style: {
        backgroundColor: "rgb(var(--v-theme-light))",
        color: "rgb(var(--v-theme-dark))",
      },
    },
    VBtnFabDark: {
      elevation: 4,
      style: {
        backgroundColor: "rgb(var(--v-theme-dark))",
        color: palette.white,
      },
    },
    VBanner: {
      style: {
        color: "rgb(var(--v-theme-dark))",
      },
      VAvatar: {
        style: {
          border: "9px solid rgb(var(--v-theme-light))",
          backgroundColor: "rgb(var(--v-theme-dark))",
          color: palette.white,
        },
      },
      VBtn: {
        class: "btn-outlined",
        rounded: "xl",
        style: {
          marginLeft: ".5em",
          backgroundColor: palette.white,
          border: "1px solid rgb(var(--v-theme-dark20))",
        },
      },
    },
    VChip: {
      class: "rounded",
      closeIcon: "mdi-close",
      selectedClass: "chip-selected",
      variant: "outlined",
      style: {
        color: "rgb(var(--v-theme-dark))",
        backgroundColor: "transparent",
        fontSize: "1rem",
      },
    },
    VChipInputGroup: {
      column: true,
      VChip: {
        selectedClass: "chip-selected",
        variant: "outlined",
        style: {
          color: "rgb(var(--v-theme-dark))",
          backgroundColor: "transparent",
          fontSize: "1rem",
        },
      },
    },
    VChipAssistGroup: {
      column: true,
      VChip: {
        selectedClass: "chip-selected",
        variant: "outlined",
        style: {
          backgroundColor: "transparent",
          color: "rgb(var(--v-theme-dark))",
          fontSize: "1rem",
        },
      },
    },
    VChipSuggestGroup: {
      column: true,
      VChip: {
        selectedClass: "chip-selected",
        variant: "outlined",
        style: {
          backgroundColor: "transparent",
          color: "rgb(var(--v-theme-dark))",
          fontSize: "1rem",
        },
      },
    },
    VChipFilterGroup: {
      column: true,
      filter: "true",
      VChip: {
        selectedClass: "chip-selected",
        variant: "outlined",
        style: {
          backgroundColor: "transparent",
          color: "rgb(var(--v-theme-dark))",
          fontSize: "1rem",
        },
      },
    },
    VChipAvatar: {
      rounded: "xl",
      selectedClass: "chip-selected",
      variant: "outlined",
      closeIcon: "mdi-close",
      style: {
        backgroundColor: "transparent",
        color: "rgb(var(--v-theme-dark))",
        fontSize: "1rem",
      },
    },
    VDialog: {
      rounded: "lg",
      style: {
        width: "70%",
      },
    },
    VDialogTooltip: {
      class: "rich-tooltip",
      locationStrategy: "connected",
      location: "end",
      attach: true,
      openOnHover: true,
      openDelay: "1000",
      style: {
        maxWidth: "30rem",
      },
    },
    VListItem: {
      rounded: "lg",
    },
    VLogoBarWhite: {
      style: {
        boxShadow: "none",
      },
      VImg: {
        maxHeight: "40%",
      },
    },
    VLogoBarDark: {
      style: {
        backgroundColor: "rgb(var(--v-theme-dark))",
        boxShadow: "none",
      },
      VImg: {
        maxHeight: "40%",
      },
    },
    VNavigationDrawer: {
      color: "dark",
      VListItem: {
        style: {
          color: palette.white,
        },
      },
    },
    VForm: {
      VBtn: {
        variant: "outlined",
        color: "dark",
        rounded: "lg",
        size: "large",
        style: {
          width: "100%",
          marginTop: ".5rem",
          marginBottom: ".5rem",
          border: "1px solid rgb(var(--v-theme-light))",
        },
      },
      style: {
        backgroundColor: palette.white,
        padding: ".5rem",
      },
    },
    VAutocomplete: {
      bgColor: "white",
      color: "primary",
      rounded: "lg",
      variant: "solo",
    },
    VCheckbox: {
      baseColor: "dark",
      hideDetails: true,
      trueIcon: "mdi-check-circle",
      ripple: true,
      style: {
        backgroundColor: palette.white,
        border: "1px solid rgb(var(--v-theme-dark20)",
        marginBottom: "1rem",
        borderRadius: "12px",
        color: "rgb(var(--v-theme-primary))",
      },
    },
    VCombobox: {
      clearable: true,
      chips: true,
      color: "primary",
      rounded: "lg",
      multiple: true,
      bgColor: "white",
      variant: "solo",
    },
    VFileInput: {
      clearable: true,
      bgColor: "white",
      color: "primary",
      rounded: "lg",
      variant: "solo",
      style: {},
    },
    VRadio: {
      style: {
        backgroundColor: palette.white,
        border: "1px solid rgb(var(--v-theme-dark20))",
        marginBottom: "1rem",
        borderRadius: "12px",
        color: "rgb(var(--v-theme-dark20))",
        opacity: "100%",
      },
    },
    VRating: {
      VBtn: {
        style: {
          border: "none",
        },
      },
    },
    VSelect: {
      bgColor: "white",
      // clearable: true,
      color: "primary",
      rounded: "lg",
      variant: "solo",
      style: {},
    },
    VSlider: {
      color: "primary",
    },
    VSnackbar: {
      color: "light",
    },
    VMultiSelect: {
      bgColor: "white",
      clearable: true,
      color: "primary",
      multiple: true,
      rounded: "lg",
      variant: "solo",
      style: {},
    },
    VPagination: {
      color: "dark",
      rounded: "circle",
    },
    VProgressCircular: {
      color: "dark",
    },
    VProgressLinearDark: {
      class: "progress-dark",
      color: "dark",
    },
    VProgressLinearLight: {
      class: "progress-light",
      color: "primary",
    },
    VSwitch: {
      color: "primary",
    },
    VSwitchInset: {
      inset: true,
      color: "primary",
    },
    VTextField: {
      clearable: true,
      bgColor: "white",
      color: "primary",
      rounded: "lg",
      variant: "solo",
    },
    VTextFieldSearch: {
      clearable: true,
      bgColor: "white",
      color: "primary",
      rounded: "lg",
      variant: "solo",
      label: "Search",
      prependInnerIcon: "mdi-magnify",
      singeLine: true,
      hideDetails: true,
      style: {
        // width: "40%",
      },
    },
    VTextarea: {
      clearable: true,
      bgColor: "white",
      color: "primary",
      rounded: "lg",
      variant: "solo",
    },
    VTabs: {
      alignTabs: "center",
      color: "primary",
    },
    VTab: {
      alignTabs: "center",
      color: "primary",
    },
    VTable: {
      style: {
        color: "rgb(var(--v-theme-dark))",
      },
    },
    VDataTable: {
      itemsPerPage: "10",
      sortAscIcon: "mdi-chevron-up",
      sortDescIcon: "mdi-chevron-down",
      hover: true,
      showSelect: true,
      density: "comfortable",
      fixedHeader: true,
      showCurrentPage: true,
      VAvatar: {
        variant: "tonal",
        style: {
          backgroundColor: "transparent",
          color: palette.gwLinkBlue,
        },
      },
      VBtn: {
        variant: "plain",
        density: "compact",
        color: "primary",
        opacity: "1",
      },
    },
    VSkeletonLoader: {},
    VSkeletonTable: {
      type: "table",
    },
    VSystemBar: {
      style: {
        color: "rgb(var(--v-theme-dark))",
      },
    },
    VCard: {
      rounded: "lg",
      VListItem: {
        style: {
          color: "rgb(var(--v-theme-dark))",
        },
      },
    },
    VCardActionLight: {
      class: "rounded-lg v-card-action v-card-action-light d-flex flex-column",
      height: "33.125rem",
      width: "19.375rem",
      link: true,
      VCardText: {
        VIcon: {
          style: {
            fontSize: "1rem",
          },
        },
      },
    },
    VCardActionDark: {
      class: "rounded-lg v-card-action v-card-action-dark d-flex flex-column",
      height: "33.125rem",
      width: "19.375rem",
      link: true,
      VCardText: {
        VIcon: {
          style: {
            fontSize: "1rem",
          },
        },
      },
    },
    VCardContentVertical: {
      class: "rounded-lg v-card-content",
      maxHeight: "25.5rem",
      width: "20.25rem",
      link: true,
      style: {
        fontFamily: palette.fontSerif,
        color: "rgb(var(--v-theme-dark))",
      },
      VCardTitle: {
        style: {
          fontSize: "1.75rem",
          lineHeight: "2.25rem",
          fontWeight: "500",
        },
      },
      VCardText: {
        style: {
          fontSize: "1.125rem",
          opacity: "70%",
        },
      },
      VImg: {
        cover: true,
      },
      VIcon: {
        style: {
          fontSize: "1rem",
        },
      },
    },
    VCardContentHorizontal: {
      class: "rounded-lg v-card-content-horizontal d-flex align-center pa-3",
      width: "20.25rem",
      link: true,
      style: {
        fontFamily: palette.fontSerif,
        color: "rgb(var(--v-theme-dark))",
        fontSize: "1.125rem",
        maxHeight: "6.5rem",
      },
      VCardTitle: {
        style: {
          lineHeight: "1.5rem",
          maxWidth: "70%",
        },
      },
      VImg: {
        cover: true,
      },
      VAvatar: {
        size: "74",
        rounded: "sm",
      },
    },
    VCardResource: {
      class: "rounded-lg v-card-resource d-flex flex-column pt-4 pl-6 pr-1",
      maxHeight: "22.5rem",
      maxWidth: "23rem",
      style: {
        color: "rgb(var(--v-theme-dark))",
      },
      VList: {
        style: {
          padding: "0",
        },
      },
      VListItem: {
        style: {
          padding: "0",
          minHeight: "2rem",
        },
      },
    },
  },
});
